import { KindToggles } from "./KindToggles";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, LogOut, Send, FileCheck2, Users, Wallet, Percent, Inbox, Star, Receipt } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { insApi, errMsg, sar, KIND_LABEL, kindText } from "../../lib/insApi";

const Stat = ({ icon: Icon, label, value, tid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={tid}>
    <Icon className="h-4 w-4 text-emerald-600" /><p className="mt-2 text-xs text-slate-500">{label}</p><p className="font-heading text-2xl font-bold">{value}</p>
  </div>
);

const Members = ({ list }) => {
  const veh = list.some((m) => m.vehicle_type || m.vehicle_number);
  const phone = list.some((m) => m.phone);
  return (
    <div className="max-h-72 overflow-auto rounded-lg border border-slate-100 bg-slate-50 text-xs" data-testid="ins-members-table">
      <table className="w-full min-w-[720px] text-start">
        <thead className="sticky top-0 bg-slate-100 text-[11px] text-slate-500"><tr>
          {["#", "الاسم", "رقم الإقامة / الحدود", "رقم الجواز", "تاريخ الميلاد", "العمر", "الجنسية", ...(veh ? ["المركبة"] : []), ...(phone ? ["الجوال"] : [])].map((h) => <th key={h} className="px-2 py-1.5 text-start font-semibold">{h}</th>)}
        </tr></thead>
        <tbody>{list.map((m, i) => (
          <tr key={i} className="border-t border-slate-100 bg-white/60">
            <td className="px-2 py-1.5 text-slate-400">{i + 1}</td>
            <td className="px-2 py-1.5 font-semibold">{m.name || "—"}</td>
            <td className="px-2 py-1.5" dir="ltr">{m.iqama_number || "—"}{m.border_number && m.border_number !== m.iqama_number ? <span className="block text-[10px] text-slate-400">حدود سابقاً: {m.border_number}</span> : null}</td>
            <td className="px-2 py-1.5" dir="ltr">{m.passport_number || "—"}</td>
            <td className="px-2 py-1.5" dir="ltr">{m.birth_date || "—"}</td>
            <td className="px-2 py-1.5">{m.age != null ? `${m.age} سنة` : "—"}</td>
            <td className="px-2 py-1.5">{m.nationality || "—"}</td>
            {veh && <td className="px-2 py-1.5">{[m.vehicle_type, m.vehicle_number].filter(Boolean).join(" · ") || "—"}</td>}
            {phone && <td className="px-2 py-1.5" dir="ltr">{m.phone || "—"}</td>}
          </tr>))}</tbody>
      </table>
    </div>
  );
};

const QuoteForm = ({ r, onDone }) => {
  const [f, setF] = useState({ price_total: "", coverage: "", network: "", deductible: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const send = async () => {
    setBusy(true);
    try { await insApi.quote(r.id, { ...f, price_total: Number(f.price_total) }); toast.success("تم إرسال العرض"); onDone(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-2">
      <Input data-testid={`quote-price-${r.id}`} type="number" placeholder="السعر الإجمالي (ريال)" value={f.price_total} onChange={set("price_total")} />
      <Input placeholder="فئة التغطية" value={f.coverage} onChange={set("coverage")} data-testid={`quote-coverage-${r.id}`} />
      <Input placeholder="شبكة المستشفيات / الورش" value={f.network} onChange={set("network")} />
      <Input placeholder="نسبة التحمّل" value={f.deductible} onChange={set("deductible")} />
      <Input className="sm:col-span-2" placeholder="ملاحظات" value={f.notes} onChange={set("notes")} />
      <Button data-testid={`quote-send-${r.id}`} disabled={!Number(f.price_total) || busy} onClick={send} className="sm:col-span-2 gap-1.5 bg-emerald-600 hover:bg-emerald-700">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} إرسال العرض</Button>
    </div>
  );
};

const IssueForm = ({ q, onDone }) => {
  const [f, setF] = useState({ policy_number: q.addition?.policy_number || "", start_date: q.addition ? new Date().toISOString().slice(0, 10) : "", expiry_date: q.addition?.expiry_date || "" });
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const send = async () => {
    try { const r = await insApi.issue(q.id, f); toast.success(`تم إصدار الوثيقة وربطها بـ ${r.linked} شخص`); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-2 sm:grid-cols-4">
      <Input data-testid={`issue-policy-${q.id}`} placeholder="رقم الوثيقة" value={f.policy_number} onChange={set("policy_number")} />
      <Input data-testid={`issue-start-${q.id}`} type="date" value={f.start_date} onChange={set("start_date")} />
      <Input data-testid={`issue-expiry-${q.id}`} type="date" value={f.expiry_date} onChange={set("expiry_date")} />
      <Button data-testid={`issue-send-${q.id}`} disabled={!f.policy_number || !f.start_date || !f.expiry_date} onClick={send} className="gap-1.5"><FileCheck2 className="h-4 w-4" /> إصدار وربط</Button>
    </div>
  );
};

export const InsurerDashboard = ({ onOut }) => {
  const [d, setD] = useState(null);
  const [openQ, setOpenQ] = useState("");
  const load = useCallback(() => insApi.dashboard().then(setD).catch(() => onOut()), [onOut]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <div className="grid min-h-screen place-items-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>;
  const s = d.stats;
  return (
    <div dir="rtl" className="min-h-screen bg-[#F3F1EC] font-body" data-testid="insurer-dashboard">
      <header className="bg-[#0B3B2E] px-5 py-5 text-white sm:px-10">
        <div className="mx-auto flex max-w-6xl items-center gap-3">
          <div><p className="text-xs text-emerald-200">لوحة مزود التأمين</p><h1 className="font-heading text-2xl font-extrabold" data-testid="insurer-name">{d.me.company_name}</h1></div>
          <button onClick={() => insApi.logout().finally(onOut)} data-testid="insurer-logout" className="mr-auto flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm hover:bg-white/10"><LogOut className="h-4 w-4" /> خروج</button>
        </div>
      </header>
      <main className="mx-auto max-w-6xl space-y-6 px-4 py-6 sm:px-10">
        <div className="grid grid-cols-2 gap-3 lg:grid-cols-6">
          <Stat icon={Star} label={`تقييم الشركات (${s.rating_count || 0})`} value={s.rating_count ? `${s.rating} / 5` : "—"} tid="ins-stat-rating" />
          <Stat icon={Send} label="عروض مقدّمة" value={s.quotes} tid="ins-stat-quotes" />
          <Stat icon={FileCheck2} label="عروض مقبولة" value={s.won} tid="ins-stat-won" />
          <Stat icon={Users} label="أشخاص مؤمَّنون" value={s.insured} tid="ins-stat-insured" />
          <Stat icon={Wallet} label="إجمالي الأقساط" value={sar(s.premium)} tid="ins-stat-premium" />
          <Stat icon={Percent} label={`عمولة المنصة (${d.me.commission_type === "fixed" ? `${d.me.commission_value} ر.س/شخص` : `${d.me.commission_value}%`})`} value={sar(s.commission)} tid="ins-stat-commission" />
        </div>
        <section className="flex flex-wrap items-center gap-3 rounded-2xl bg-white p-4" data-testid="insurer-services">
          <div><p className="font-heading font-bold">الخدمات التي تستقبل طلباتها</p><p className="text-xs text-slate-500">لن تصلك إلا طلبات الخدمات المفعّلة</p></div>
          <div className="ms-auto"><KindToggles kinds={d.me.kinds} tid="insurer-kinds" onSave={(k) => insApi.setKinds(k).then(load)} /></div>
        </section>
        <section className="space-y-3">
          <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Inbox className="h-5 w-5 text-emerald-700" /> طلبات مفتوحة ({d.open.length})</h2>
          {d.open.length === 0 && <p className="rounded-2xl bg-white p-6 text-center text-sm text-slate-400">لا توجد طلبات مفتوحة حالياً</p>}
          {d.open.map((r) => (
            <div key={r.id} className="space-y-2 rounded-2xl bg-white p-4" data-testid={`ins-open-${r.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm"><b>{kindText(r)}</b> · {r.count} شخص
                {r.channel === "rabet" && <span className="rounded-full bg-[#123A5C]/10 px-2 py-0.5 text-xs text-[#123A5C]">تأمين ربط</span>}
                {r.addition && <span className="rounded-full bg-sky-50 px-2 py-0.5 text-xs text-sky-700">إضافة لبوليصتكم {r.addition.policy_number} — {r.org_name}</span>}
                <span className="text-xs text-slate-400">آخر موعد {new Date(r.deadline).toLocaleString("ar-SA-u-nu-latn", { timeZone: "Asia/Riyadh", hour: "2-digit", minute: "2-digit", day: "numeric", month: "short" })}</span>
                {r.quoted ? <span className="mr-auto rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">تم تقديم عرض</span>
                  : <Button size="sm" variant="outline" className="mr-auto" data-testid={`ins-open-quote-${r.id}`} onClick={() => setOpenQ(openQ === r.id ? "" : r.id)}>تقديم عرض</Button>}
              </div>
              {r.notes && <p className="text-xs text-slate-600">ملاحظات: {r.notes}</p>}
              <Members list={r.members} />
              {openQ === r.id && !r.quoted && <QuoteForm r={r} onDone={() => { setOpenQ(""); load(); }} />}
            </div>
          ))}
        </section>
        {d.invoices?.length > 0 && (
          <section className="space-y-2" data-testid="ins-invoices">
            <h2 className="flex items-center gap-2 font-heading text-lg font-bold"><Receipt className="h-5 w-5 text-emerald-700" /> فواتير عمولة المنصة</h2>
            <div className="overflow-x-auto rounded-2xl bg-white"><table className="w-full text-sm"><thead className="bg-slate-50 text-xs text-slate-500"><tr>
              <th className="p-2.5 text-right">الفاتورة</th><th className="p-2.5">الشهر</th><th className="p-2.5">الوثائق</th><th className="p-2.5">المبلغ</th><th className="p-2.5">المدفوع</th><th className="p-2.5">الحالة</th></tr></thead>
              <tbody>{d.invoices.map((i) => (
                <tr key={i.id} className="border-t border-slate-100 text-center" data-testid={`ins-invoice-${i.id}`}>
                  <td className="p-2.5 text-right font-mono-nums" dir="ltr">{i.number}</td><td>{i.period}</td><td>{i.items.length}</td><td dir="ltr">{sar(i.total)}</td><td dir="ltr">{sar(i.paid)}</td>
                  <td><span className={`rounded-full px-2 py-0.5 text-xs ${i.status === "paid" ? "bg-emerald-50 text-emerald-700" : i.status === "partial" ? "bg-amber-50 text-amber-700" : "bg-rose-50 text-rose-700"}`}>{{ paid: "مدفوعة", partial: "مدفوعة جزئياً", unpaid: "غير مدفوعة" }[i.status]}</span></td>
                </tr>))}</tbody></table></div>
          </section>
        )}
        <section className="space-y-3">
          <h2 className="font-heading text-lg font-bold">عروضي</h2>
          {d.quotes.length === 0 && <p className="rounded-2xl bg-white p-6 text-center text-sm text-slate-400">لم تقدّم عروضاً بعد</p>}
          {d.quotes.map((q) => (
            <div key={q.id} className="space-y-2 rounded-2xl bg-white p-4" data-testid={`ins-quote-${q.id}`}>
              <div className="flex flex-wrap items-center gap-2 text-sm"><b>{kindText(q)}</b> · {q.member_count} شخص · <span dir="ltr">{sar(q.price_total)}</span>
                {q.org_name && <span className="font-semibold text-emerald-800">{q.org_name}</span>}
                <span className="mr-auto rounded-full bg-slate-100 px-2 py-0.5 text-xs">{{ submitted: "بانتظار قرار العميل", accepted: "مقبول — أصدر الوثيقة", issued: `صادرة ${q.policy_number}`, rejected: "لم يُقبل", cancelled: "ألغى العميل الطلب" }[q.status]}</span>
              </div>
              {q.contact && <p className="text-xs text-slate-600">التواصل: {q.contact.name} · <span dir="ltr">{q.contact.email}</span> {q.contact.phone && <span dir="ltr">· {q.contact.phone}</span>}</p>}
              {(q.status === "accepted" || q.status === "issued") && <Members list={q.members} />}
              {q.status === "accepted" && <IssueForm q={q} onDone={load} />}
              {q.status === "issued" && <p className="text-xs text-slate-500">عمولة المنصة: {sar(q.commission)}{q.rating ? ` · تقييم العميل: ${"★".repeat(q.rating)}${q.rating_comment ? ` «${q.rating_comment}»` : ""}` : ""}</p>}
            </div>
          ))}
        </section>
      </main>
    </div>
  );
};
